import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Explainer,
  Flex,
  OneHalfLayout,
  PageWrapper,
  Panel,
  Placeholder,
  Button,
  Label,
  TextInput,
  useNotification,
  Span
} from '@ubisend/pulse-components';
import { useQuery, useMutation, useQueryClient } from '@ubisend/pulse-hooks';
import { PolicyAccept, BackButton } from '@ubisend/pulse-channels';
import { useCopyToClipboard } from '@ubisend/pulse-component-hooks';

import {
  acceptPolicy as acceptPolicyApi,
  updateSettings as updateSettingsApi
} from '../api/index';
import PropTypes from 'prop-types';

const VoiceCallDataForm = ({ url, noSecret }) => {
  const [apiKey, setApiKey] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [transferFrom, setTransferFrom] = useState('');
  const [voice, setVoice] = useState('');
  const [voiceSettings, setVoiceSettings] = useState('{}');

  const [showApiKey, setShowApiKey] = useState(noSecret);

  const { showSuccess } = useNotification();
  const { copy, copied } = useCopyToClipboard();
  const queryClient = useQueryClient();

  const handleCopy = () => copy(url);

  const { mutate: updateSettings } = useMutation({
    mutationFn: updateSettingsApi,
    onSuccess: () => {
      showSuccess('Successfully updated voice call settings');
      queryClient.invalidateQueries('voice-call/settings');
    }
  });

  const handleSubmit = () => {
    const payload = {};

    if (apiKey.length > 0) {
      payload.api_key = apiKey;
    }

    if (publicKey.length > 0) {
      payload.public_key = publicKey;
    }

    if (transferFrom.length > 0) {
      payload.transfer_from = transferFrom;
    }

    if (voice.length > 0) {
      payload.voice = voice;
    }

    if (voiceSettings.length > 0) {
      payload.voice_settings = voiceSettings;
    }

    updateSettings(payload);
  };

  return (
    <Flex col>
      <Flex col>
        <Label htmlFor="api">API endpoint</Label>
        <Flex>
          <TextInput id="api" disabled value={url} />
          <Button
            variant="primary"
            disabled={copied}
            icon="clipBoard"
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Flex>
      </Flex>

      <Flex col mt>
        {showApiKey && (
          <Label htmlFor="api_key_input">API Key</Label>
        )}

        <Flex between center>
          {showApiKey ? (
            <TextInput
              id="api_key_input"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Your API key"
            />
          ) : (
              <Span>API key and public key have been set</Span>
          )}

          {!showApiKey && (
            <Flex top>
              <Button onClick={() => setShowApiKey(true)}>
                Change Credentials
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      {showApiKey && (
        <>
          <Flex col mt>
            <Label htmlFor="public_key_input">Public Key</Label>
            <TextInput
              id="public_key_input"
              value={publicKey}
              onChange={e => setPublicKey(e.target.value)}
              placeholder="Your public key"
            />
          </Flex>

          <Flex col mt>
            <Label htmlFor="transfer_from_input">Transfer From</Label>
            <TextInput
              id="transfer_from_input"
              value={transferFrom}
              onChange={e => setTransferFrom(e.target.value)}
              placeholder="e.g. +44123456789"
            />
          </Flex>

          <Flex col mt>
            <Label htmlFor="voice_input">Voice</Label>
            <TextInput
              id="voice_input"
              value={voice}
              onChange={e => setVoice(e.target.value)}
              placeholder="e.g. Telnyx.KokoroTTS.af_heart"
            />
          </Flex>

          <Flex col mt>
            <Label htmlFor="voice_settings_input">Voice Setting</Label>
            <TextInput
              id="voice_settings_input"
              value={voiceSettings}
              onChange={e => setVoiceSettings(e.target.value)}
              placeholder={`e.g. {" api_key_ref": "your_api_key_ref"}`}
            />
            <span small>JSON value only, this is setting for voice.</span>
          </Flex>

          <Flex right mt>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                apiKey.length === 0 ||
                publicKey.length === 0 ||
                transferFrom.length === 0 ||
                voice.length === 0 ||
                voiceSettings.length === 0
              }
            >
              Save
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
};

VoiceCallDataForm.propTypes = {
  url: PropTypes.string,
  noSecret: PropTypes.bool,
};

const VoiceCallLink = () => {
  const query = useQuery({
    queryKey: ['voice-call/settings']
  });

  const queryClient = useQueryClient();

  const { mutate: acceptPolicy } = useMutation({
    mutationFn: acceptPolicyApi,
    onSuccess: () => {
      queryClient.invalidateQueries('voice-call/settings');
    }
  });

  const PolicyCheck = () => {
    if (!query.data.data.has_given_data_consent) {
      return (
        <PolicyAccept
          handlePolicyAccept={acceptPolicy}
          policyUrl="https://sopranodesign.com/privacy/"
        />
      );
    }

    return (
      <Panel divider header="Connect your chatbot to Voice Call all over IP">
        <Flex col>
          <Explainer mb>
            Copy the link below into your Voice Call AI platform. Follow our{' '}
            <Link to="/docs/2151317744/2223964202">documentation</Link> to set up
            your first Voice Call AI chatbot.
          </Explainer>

          <VoiceCallDataForm
            url={query.data.data.url}
            noSecret={query.data.data.has_not_api_key}
          />
        </Flex>
      </Panel>
    );
  };

  return (
    <PageWrapper
      center
      half
      header="Voice Call over IP"
      prevAction={<BackButton to="/channels" />}
    >
      <Flex col center>
        <OneHalfLayout>
          {query.isLoading && <Placeholder items={1} subitems={2} />}
          {query.isSuccess && <PolicyCheck />}
        </OneHalfLayout>
      </Flex>
    </PageWrapper>
  );
};

export default VoiceCallLink;